import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiImage, FiDollarSign, FiMail, FiShield, FiBell, FiLayout, FiCheckCircle } = FiIcons;

const SettingsModal = ({ onClose }) => {
  const { appSettings, saveAppSettings, currencies = [], messageTemplates } = useData();
  const [activeTab, setActiveTab] = useState('general');
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({ ...appSettings });
  const [loading, setLoading] = useState(false);

  const emailTemplates = messageTemplates.filter(t => t.type === 'email');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await saveAppSettings(settings);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      alert("Failed to save: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSettings(prev => ({
        ...prev,
        currency: currency.code,
        currencySymbol: currency.symbol,
        exchangeRate: currency.rate
      }));
    }
  };

  const tabs = [
    { id: 'general', label: 'Branding', icon: FiLayout },
    { id: 'billing', label: 'Currency', icon: FiDollarSign },
    { id: 'email', label: 'Communications', icon: FiMail }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full h-[80vh] flex overflow-hidden border border-white/20"
      >
        {/* Sidebar */}
        <div className="w-72 bg-gray-50 border-r border-gray-100 p-8 flex flex-col">
          <div className="mb-10">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">System</h2>
            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Configuration v2.0</p>
          </div>

          <nav className="space-y-2 flex-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
                  activeTab === tab.id 
                    ? 'bg-white text-indigo-600 shadow-xl shadow-indigo-100 scale-105' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={tab.icon} />
                {tab.label}
              </button>
            ))}
          </nav>

          <button onClick={onClose} className="py-4 text-gray-400 font-bold text-[10px] uppercase tracking-widest hover:text-gray-600">
            Discard Changes
          </button>
        </div>

        {/* Content Area */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col bg-white">
          <div className="flex-1 overflow-y-auto p-12">
            <AnimatePresence mode="wait">
              {activeTab === 'general' && (
                <motion.div key="general" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-10">
                  <div>
                    <label className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-6 block">Visual Identity</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 ml-1">Company Name</label>
                        <input 
                          type="text" 
                          value={settings.companyName || ''} 
                          onChange={(e) => setSettings({...settings, companyName: e.target.value})}
                          className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 ml-1">Brand Theme Color</label>
                        <div className="flex gap-3">
                          <input 
                            type="color" 
                            value={settings.brandColor || '#4f46e5'} 
                            onChange={(e) => setSettings({...settings, brandColor: e.target.value})}
                            className="w-16 h-14 rounded-xl border-none cursor-pointer"
                          />
                          <input 
                            type="text" 
                            value={settings.brandColor || '#4f46e5'} 
                            onChange={(e) => setSettings({...settings, brandColor: e.target.value})}
                            className="flex-1 p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-mono font-bold"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'billing' && (
                <motion.div key="billing" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="space-y-10">
                  <label className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-6 block">Regional Settings</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 ml-1">Default Currency</label>
                      <select 
                        value={settings.currency || 'USD'} 
                        onChange={(e) => handleCurrencyChange(e.target.value)}
                        className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-600 outline-none font-bold"
                      >
                        {currencies.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name} ({c.symbol})</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 ml-1">Exchange Rate (vs USD)</label>
                      <input 
                        type="number" step="0.01"
                        value={settings.exchangeRate || 1} 
                        onChange={(e) => setSettings({...settings, exchangeRate: parseFloat(e.target.value)})}
                        className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-600 outline-none font-bold"
                      />
                    </div>
                  </div>
                  <div className="bg-indigo-50 p-8 rounded-[2.5rem] border-2 border-indigo-100/50">
                    <p className="text-xs font-bold text-indigo-900 leading-relaxed mb-2 uppercase tracking-widest opacity-60">Preview Calculation</p>
                    <p className="text-3xl font-black text-indigo-600">
                      100 USD = {settings.currencySymbol}{(100 * settings.exchangeRate).toFixed(2)}
                    </p>
                  </div>
                </motion.div>
              )}

              {activeTab === 'email' && (
                <motion.div key="email" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">
                  <label className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-6 block">Communication Templates</label>
                  
                  <div className="grid grid-cols-1 gap-6">
                    {/* Welcome Email */}
                    <div className="p-6 bg-gray-50 rounded-[2rem] border-2 border-transparent hover:border-indigo-100 transition-all flex items-center gap-6">
                      <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 flex-shrink-0">
                        <SafeIcon icon={FiLayout} className="text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-gray-900 text-sm">Welcome Workflow</h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Sent to new accounts</p>
                        <select 
                          value={settings.welcomeTemplateId || ''} 
                          onChange={(e) => setSettings({...settings, welcomeTemplateId: e.target.value})}
                          className="w-full mt-3 p-3 bg-white border-2 border-gray-100 rounded-xl focus:border-indigo-600 outline-none font-bold text-sm"
                        >
                          <option value="">System Default</option>
                          {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Password Reset */}
                    <div className="p-6 bg-gray-50 rounded-[2rem] border-2 border-transparent hover:border-rose-100 transition-all flex items-center gap-6">
                      <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center text-rose-600 flex-shrink-0">
                        <SafeIcon icon={FiShield} className="text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-gray-900 text-sm">Security Alerts</h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Forgot password & updates</p>
                        <select 
                          value={settings.resetPasswordTemplateId || ''} 
                          onChange={(e) => setSettings({...settings, resetPasswordTemplateId: e.target.value})}
                          className="w-full mt-3 p-3 bg-white border-2 border-gray-100 rounded-xl focus:border-indigo-600 outline-none font-bold text-sm"
                        >
                          <option value="">Firebase Standard</option>
                          {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Custom Reminders */}
                    <div className="p-6 bg-gray-50 rounded-[2rem] border-2 border-transparent hover:border-amber-100 transition-all flex items-center gap-6">
                      <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 flex-shrink-0">
                        <SafeIcon icon={FiBell} className="text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-gray-900 text-sm">Automated Reminders</h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Payment & Session alerts</p>
                        <select 
                          value={settings.reminderTemplateId || ''} 
                          onChange={(e) => setSettings({...settings, reminderTemplateId: e.target.value})}
                          className="w-full mt-3 p-3 bg-white border-2 border-gray-100 rounded-xl focus:border-indigo-600 outline-none font-bold text-sm"
                        >
                          <option value="">Standard Reminder</option>
                          {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="p-10 border-t border-gray-100 bg-gray-50/50 flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="px-12 py-4 bg-indigo-600 text-white rounded-2xl shadow-xl shadow-indigo-100 font-black text-xs uppercase tracking-widest flex items-center gap-3 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : success ? (
                <><SafeIcon icon={FiCheckCircle} /> Saved!</>
              ) : (
                <><SafeIcon icon={FiSave} /> Update Configuration</>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default SettingsModal;